<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { generateStatisticsFileName } from 'dashboard/helper/downloadHelper';
import ReportsAPI from 'dashboard/api/reports';
import { formatReportDuration, durationMetricTooltip } from './constants';
import ReportHeader from './components/ReportHeader.vue';
import ReportFiltersSection from './components/ReportFiltersSection.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import ChatsWeekdayHeatmap from './components/ChatsWeekdayHeatmap.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useSummaryReportArchive } from './composables/useSummaryReportArchive';
import { buildBucketArchiveFilters } from './helpers/summaryReportArchiveHelper';
import {
  resolveDynamicsGranularity,
  formatPeriodRangeLabel,
} from './helpers/dynamicsGranularityHelper';

const { t } = useI18n();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const { openArchive, resolveMetricKeyFromCardKey } = useSummaryReportArchive();

const from = ref(0);
const to = ref(0);
const comparisonPeriods = ref([]);
const entityFilters = ref({});
const result = ref('all');
const entryType = ref('all');
const summaryGroupBy = ref('source');
const heatmapMode = ref('average');
const loading = ref(false);
const report = ref({
  overview: {},
  dynamics: [],
  wait_buckets: [],
  heatmap: [],
  outcomes: {},
  summary: [],
  comparisons: [],
});

const resultOptions = computed(() => [
  { value: 'all', label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.RESULT_ALL') },
  {
    value: 'accepted',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.RESULT_ACCEPTED'),
  },
  {
    value: 'abandoned',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.RESULT_ABANDONED'),
  },
  {
    value: 'waiting',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.RESULT_WAITING'),
  },
]);

const entryTypeOptions = computed(() => [
  { value: 'all', label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.ENTRY_ALL') },
  {
    value: 'bot_handoff',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.ENTRY_BOT_HANDOFF'),
  },
  {
    value: 'requeue',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.ENTRY_REQUEUE'),
  },
]);

const requestPayload = computed(() => ({
  from: from.value,
  to: to.value,
  ...entityFilters.value,
  result: result.value,
  entryType: entryType.value,
  summaryGroupBy: summaryGroupBy.value,
  comparisonPeriods: comparisonPeriods.value,
}));

const formatCount = value =>
  value || value === 0 ? Number(value).toLocaleString() : '0';

const formatWait = value =>
  value === null || value === undefined ? '—' : formatReportDuration(value);

const formatRate = value =>
  value === null || value === undefined ? '—' : `${value}%`;

const formatPeriodLabel = (since, until) => {
  const label = formatPeriodRangeLabel(since, until);
  return label ? `${label} UTC` : '';
};

const comparisonLabels = computed(() =>
  (comparisonPeriods.value || []).map(period =>
    formatPeriodLabel(period.from ?? period.since, period.to ?? period.until)
  )
);

const hasData = computed(() => (report.value.overview?.queue_entries || 0) > 0);

const overviewCards = computed(() => {
  const o = report.value.overview || {};
  const comparisons = report.value.comparisons || [];
  const formatComparisonValue = (key, overview) => {
    if (key === 'median_wait' || key === 'max_wait') {
      return formatWait(overview?.[key]);
    }
    return formatCount(overview?.[key]);
  };
  const buildComparisons = key =>
    comparisons.map((slice, index) => ({
      value: formatComparisonValue(key, slice.overview),
      periodLabel: comparisonLabels.value[index] || '',
    }));

  return [
    {
      key: 'entries',
      label: t('QUEUED_CUSTOMERS_REPORTS.CARDS.QUEUE_ENTRIES'),
      value: formatCount(o.queue_entries),
      tooltip: durationMetricTooltip(
        t,
        t('QUEUED_CUSTOMERS_REPORTS.CARDS.TOOLTIPS.QUEUE_ENTRIES')
      ),
      comparisons: buildComparisons('queue_entries'),
    },
    {
      key: 'unique',
      label: t('QUEUED_CUSTOMERS_REPORTS.CARDS.UNIQUE_INQUIRIES'),
      value: formatCount(o.unique_inquiries),
      tooltip: durationMetricTooltip(
        t,
        t('QUEUED_CUSTOMERS_REPORTS.CARDS.TOOLTIPS.UNIQUE_INQUIRIES')
      ),
      comparisons: buildComparisons('unique_inquiries'),
    },
    {
      key: 'accepted',
      label: t('QUEUED_CUSTOMERS_REPORTS.CARDS.ACCEPTED'),
      value: formatCount(o.accepted),
      tooltip: durationMetricTooltip(
        t,
        t('QUEUED_CUSTOMERS_REPORTS.CARDS.TOOLTIPS.ACCEPTED')
      ),
      comparisons: buildComparisons('accepted'),
    },
    {
      key: 'abandoned',
      label: t('QUEUED_CUSTOMERS_REPORTS.CARDS.ABANDONED'),
      value: formatCount(o.abandoned),
      tooltip: durationMetricTooltip(
        t,
        t('QUEUED_CUSTOMERS_REPORTS.CARDS.TOOLTIPS.ABANDONED')
      ),
      comparisons: buildComparisons('abandoned'),
    },
    {
      key: 'waiting',
      label: t('QUEUED_CUSTOMERS_REPORTS.CARDS.WAITING'),
      value: formatCount(o.waiting_at_end),
      tooltip: durationMetricTooltip(
        t,
        t('QUEUED_CUSTOMERS_REPORTS.CARDS.TOOLTIPS.WAITING')
      ),
      comparisons: buildComparisons('waiting_at_end'),
    },
    {
      key: 'median',
      label: t('QUEUED_CUSTOMERS_REPORTS.CARDS.MEDIAN_WAIT'),
      value: formatWait(o.median_wait),
      tooltip: durationMetricTooltip(
        t,
        t('QUEUED_CUSTOMERS_REPORTS.CARDS.TOOLTIPS.MEDIAN_WAIT'),
        o.median_wait
      ),
      comparisons: buildComparisons('median_wait'),
    },
    {
      key: 'max',
      label: t('QUEUED_CUSTOMERS_REPORTS.CARDS.MAX_WAIT'),
      value: formatWait(o.max_wait),
      tooltip: durationMetricTooltip(
        t,
        t('QUEUED_CUSTOMERS_REPORTS.CARDS.TOOLTIPS.MAX_WAIT'),
        o.max_wait
      ),
      conversationId: o.max_wait_conversation_id,
      comparisons: buildComparisons('max_wait'),
    },
    {
      key: 'peak',
      label: t('QUEUED_CUSTOMERS_REPORTS.CARDS.PEAK_SIZE'),
      value: formatCount(o.peak_queue_size),
      tooltip: durationMetricTooltip(
        t,
        t('QUEUED_CUSTOMERS_REPORTS.CARDS.TOOLTIPS.PEAK_SIZE')
      ),
      comparisons: buildComparisons('peak_queue_size'),
    },
  ];
});

const dynamicsCollection = computed(() => {
  const labels = (report.value.dynamics || []).map(row => row.date);
  const datasets = [
    {
      label: t('QUEUED_CUSTOMERS_REPORTS.CHARTS.ENTERED'),
      data: (report.value.dynamics || []).map(row => row.entered),
      borderColor: '#3B82F6',
      backgroundColor: '#3B82F6',
      tension: 0.3,
    },
    {
      label: t('QUEUED_CUSTOMERS_REPORTS.CHARTS.ACCEPTED'),
      data: (report.value.dynamics || []).map(row => row.accepted),
      borderColor: '#10B981',
      backgroundColor: '#10B981',
      tension: 0.3,
    },
    {
      label: t('QUEUED_CUSTOMERS_REPORTS.CHARTS.ABANDONED'),
      data: (report.value.dynamics || []).map(row => row.abandoned),
      borderColor: '#F97316',
      backgroundColor: '#F97316',
      tension: 0.3,
    },
  ];

  (report.value.comparisons || []).forEach((slice, index) => {
    const byDate = Object.fromEntries(
      (slice.dynamics || []).map(row => [row.date, row])
    );
    const suffix = comparisonLabels.value[index]
      ? ` (${comparisonLabels.value[index]})`
      : ` (${index + 1})`;
    datasets.push(
      {
        label: `${t('QUEUED_CUSTOMERS_REPORTS.CHARTS.ENTERED')}${suffix}`,
        data: labels.map(date => byDate[date]?.entered || 0),
        borderColor: '#93C5FD',
        backgroundColor: '#93C5FD',
        borderDash: [4, 4],
        tension: 0.3,
      },
      {
        label: `${t('QUEUED_CUSTOMERS_REPORTS.CHARTS.ACCEPTED')}${suffix}`,
        data: labels.map(date => byDate[date]?.accepted || 0),
        borderColor: '#86EFAC',
        backgroundColor: '#86EFAC',
        borderDash: [4, 4],
        tension: 0.3,
      },
      {
        label: `${t('QUEUED_CUSTOMERS_REPORTS.CHARTS.ABANDONED')}${suffix}`,
        data: labels.map(date => byDate[date]?.abandoned || 0),
        borderColor: '#FDBA74',
        backgroundColor: '#FDBA74',
        borderDash: [4, 4],
        tension: 0.3,
      }
    );
  });

  return { labels, datasets };
});

const waitBucketsCollection = computed(() => ({
  labels: (report.value.wait_buckets || []).map(row => row.label),
  datasets: [
    {
      label: t('QUEUED_CUSTOMERS_REPORTS.CHARTS.WAIT_TIME'),
      data: (report.value.wait_buckets || []).map(row => row.count),
      backgroundColor: '#6366F1',
    },
  ],
}));

const outcomesCollection = computed(() => ({
  labels: [t('QUEUED_CUSTOMERS_REPORTS.CHARTS.OUTCOMES')],
  datasets: [
    {
      label: t('QUEUED_CUSTOMERS_REPORTS.CHARTS.ACCEPTED'),
      data: [report.value.outcomes?.accepted || 0],
      backgroundColor: '#10B981',
      stack: 'outcomes',
    },
    {
      label: t('QUEUED_CUSTOMERS_REPORTS.CHARTS.ABANDONED'),
      data: [report.value.outcomes?.abandoned || 0],
      backgroundColor: '#F97316',
      stack: 'outcomes',
    },
    {
      label: t('QUEUED_CUSTOMERS_REPORTS.CHARTS.WAITING'),
      data: [report.value.outcomes?.waiting_at_end || 0],
      backgroundColor: '#64748B',
      stack: 'outcomes',
    },
  ],
}));

const heatmapPoints = computed(() =>
  (report.value.heatmap || []).map(row => ({
    weekday: row.weekday,
    hour: row.hour,
    value:
      heatmapMode.value === 'peak'
        ? row.peak || 0
        : (row.average_concurrent ?? row.average ?? 0),
  }))
);

const stackedOptions = {
  scales: {
    x: { stacked: true, grid: { drawOnChartArea: false } },
    y: { stacked: true, beginAtZero: true, grid: { drawOnChartArea: false } },
  },
};

const fetchReport = async () => {
  if (!from.value || !to.value) return;
  loading.value = true;
  try {
    const { data } = await ReportsAPI.getQueuedCustomers(requestPayload.value);
    report.value = {
      overview: {},
      dynamics: [],
      wait_buckets: [],
      heatmap: [],
      outcomes: {},
      summary: [],
      comparisons: [],
      ...data,
    };
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    loading.value = false;
  }
};

const onFiltersApply = ({ date, entity }) => {
  from.value = date.from;
  to.value = date.to;
  comparisonPeriods.value = date.comparisonPeriods || [];
  entityFilters.value = entity;
  if (!result.value) result.value = 'all';
  if (!entryType.value) entryType.value = 'all';
  fetchReport();
};

const downloadCsv = async () => {
  try {
    await ReportsAPI.downloadSectionCsv(
      'queue',
      requestPayload.value,
      generateStatisticsFileName({
        section: 'queue',
        from: from.value,
        to: to.value,
      })
    );
  } catch {
    // Error toast is shown by downloadStatisticsCsvAsync.
  }
};

const archiveContext = computed(() => ({
  from: from.value,
  to: to.value,
  reportType: 'queue',
  entityFilters: {
    ...entityFilters.value,
    result: result.value,
    entryType: entryType.value,
  },
}));

const openConversation = conversationId => {
  if (!conversationId) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: conversationId,
    },
  });
};

const dynamicsGranularity = computed(() =>
  resolveDynamicsGranularity(from.value, to.value)
);

const QUEUE_DYNAMICS_METRICS = [
  'queueEntriesCount',
  'queueAcceptedCount',
  'queueAbandonedCount',
];

const QUEUE_OUTCOME_METRICS = [
  'queueAcceptedCount',
  'queueAbandonedCount',
  'queueWaitingCount',
];

const onCardClick = card => {
  if (card.key === 'max' && card.conversationId) {
    openConversation(card.conversationId);
    return;
  }

  if (card.key === 'peak') {
    openArchive({
      metricKey: 'queuePeakCount',
      reportType: 'queue',
      row: null,
      context: archiveContext.value,
    });
    return;
  }

  const metricKey = resolveMetricKeyFromCardKey('queue', card.key);
  if (!metricKey) return;
  openArchive({
    metricKey,
    reportType: 'queue',
    row: null,
    context: archiveContext.value,
  });
};

const onDynamicsPointClick = event => {
  const metricKey = QUEUE_DYNAMICS_METRICS[event.datasetIndex % 3];
  const dateLabel = (report.value.dynamics || [])[event.index]?.date;
  if (!metricKey || !dateLabel) return;

  const filters = buildBucketArchiveFilters({
    metricKey,
    reportType: 'queue',
    row: null,
    context: archiveContext.value,
    dateLabel,
    granularity: dynamicsGranularity.value,
  });

  openArchive({
    metricKey,
    reportType: 'queue',
    row: null,
    context: archiveContext.value,
    filters,
  });
};

const onWaitBucketsClick = event => {
  const row = (report.value.wait_buckets || [])[event.index];
  if (!row?.count) return;

  openArchive({
    metricKey: 'queueAcceptedCount',
    reportType: 'queue',
    row: null,
    context: archiveContext.value,
    durationMin: row.min,
    durationMax: row.max == null ? undefined : row.max,
    durationEvent: 'queue_wait',
  });
};

const onOutcomesBarClick = event => {
  const metricKey = QUEUE_OUTCOME_METRICS[event.datasetIndex];
  if (!metricKey) return;
  openArchive({
    metricKey,
    reportType: 'queue',
    row: null,
    context: archiveContext.value,
  });
};

const onSummaryRowClick = row => {
  const nextFilters = { ...archiveContext.value.entityFilters };
  if (row.group_type === 'source' && row.id) {
    nextFilters.inboxIds = [row.id];
  } else if (row.group_type === 'team' && row.id) {
    nextFilters.teamIds = [row.id];
  }
  openArchive({
    metricKey: 'queueEntriesCount',
    reportType: 'queue',
    row: null,
    context: {
      ...archiveContext.value,
      entityFilters: nextFilters,
    },
  });
};

const onMaxWaitClick = (event, conversationId) => {
  event.stopPropagation();
  openConversation(conversationId);
};

watch([result, entryType], () => {
  if (!result.value) result.value = 'all';
  if (!entryType.value) entryType.value = 'all';
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <ReportHeader :header-title="$t('QUEUED_CUSTOMERS_REPORTS.HEADER')">
      <V4Button
        :label="$t('QUEUED_CUSTOMERS_REPORTS.DOWNLOAD')"
        icon="i-ph-file-arrow-down"
        size="sm"
        @click="downloadCsv"
      />
    </ReportHeader>

    <p class="text-sm text-n-slate-11">
      {{ $t('QUEUED_CUSTOMERS_REPORTS.DESCRIPTION') }}
    </p>

    <ReportFiltersSection
      type="queue"
      :disabled="loading"
      hide-resolution-method
      @filters-apply="onFiltersApply"
    >
      <div class="mt-2 flex flex-wrap gap-3">
        <div class="min-w-[12rem]">
          <ComboBox
            v-model="result"
            :options="resultOptions"
            :placeholder="$t('QUEUED_CUSTOMERS_REPORTS.FILTERS.RESULT')"
          />
        </div>
        <div class="min-w-[12rem]">
          <ComboBox
            v-model="entryType"
            :options="entryTypeOptions"
            :placeholder="$t('QUEUED_CUSTOMERS_REPORTS.FILTERS.ENTRY_TYPE')"
          />
        </div>
      </div>
    </ReportFiltersSection>

    <p class="text-xs text-n-slate-11">
      {{ $t('QUEUED_CUSTOMERS_REPORTS.COHORT_NOTE') }}
    </p>

    <div
      class="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-8 rounded-xl bg-n-solid-2 px-4 py-4 outline outline-1 outline-n-container"
    >
      <button
        v-for="card in overviewCards"
        :key="card.key"
        type="button"
        class="flex cursor-pointer flex-col gap-1 text-left"
        @click="onCardClick(card)"
      >
        <span class="text-xs text-n-slate-11 inline-flex items-center gap-1">
          {{ card.label }}
          <span
            v-if="card.tooltip"
            v-tooltip.right="card.tooltip"
            class="i-lucide-info flex-shrink-0 text-n-slate-10 size-3"
          />
        </span>
        <span class="text-xl font-medium text-n-slate-12">{{
          card.value
        }}</span>
        <span
          v-for="(comp, idx) in card.comparisons"
          :key="`${card.key}-${idx}`"
          class="text-[11px] text-n-slate-10"
        >
          {{ comp.value }}
          <span v-if="comp.periodLabel">{{ ` · ${comp.periodLabel}` }}</span>
        </span>
      </button>
    </div>

    <div
      v-if="!loading && !hasData"
      class="rounded-xl bg-n-solid-2 px-5 py-10 text-center text-sm text-n-slate-11 outline outline-1 outline-n-container"
    >
      {{ $t('QUEUED_CUSTOMERS_REPORTS.EMPTY') }}
    </div>

    <template v-else>
      <div class="grid gap-4 lg:grid-cols-2">
        <section
          class="rounded-xl bg-n-solid-2 px-5 py-4 outline outline-1 outline-n-container"
        >
          <h3 class="mb-3 text-base font-medium text-n-slate-12">
            {{ $t('QUEUED_CUSTOMERS_REPORTS.CHARTS.DYNAMICS') }}
          </h3>
          <div class="h-64">
            <LineChart
              :collection="dynamicsCollection"
              show-legend
              @point-click="onDynamicsPointClick"
            />
          </div>
        </section>

        <section
          class="rounded-xl bg-n-solid-2 px-5 py-4 outline outline-1 outline-n-container"
        >
          <h3 class="mb-3 text-base font-medium text-n-slate-12">
            {{ $t('QUEUED_CUSTOMERS_REPORTS.CHARTS.WAIT_TIME') }}
          </h3>
          <div class="h-64">
            <BarChart
              :collection="waitBucketsCollection"
              @bar-click="onWaitBucketsClick"
            />
          </div>
        </section>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <ChatsWeekdayHeatmap
          :points="heatmapPoints"
          :title="$t('QUEUED_CUSTOMERS_REPORTS.HEATMAP_TITLE')"
          :archive-context="archiveContext"
          metric-key="queueEntriesCount"
        >
          <template #actions>
            <div class="flex gap-1">
              <button
                type="button"
                class="rounded-lg px-2.5 py-1 text-xs"
                :class="
                  heatmapMode === 'average'
                    ? 'bg-n-brand text-white'
                    : 'border border-n-weak text-n-slate-11'
                "
                @click="heatmapMode = 'average'"
              >
                {{ $t('QUEUED_CUSTOMERS_REPORTS.HEATMAP_AVG') }}
              </button>
              <button
                type="button"
                class="rounded-lg px-2.5 py-1 text-xs"
                :class="
                  heatmapMode === 'peak'
                    ? 'bg-n-brand text-white'
                    : 'border border-n-weak text-n-slate-11'
                "
                @click="heatmapMode = 'peak'"
              >
                {{ $t('QUEUED_CUSTOMERS_REPORTS.HEATMAP_PEAK') }}
              </button>
            </div>
          </template>
        </ChatsWeekdayHeatmap>

        <section
          class="rounded-xl bg-n-solid-2 px-5 py-4 outline outline-1 outline-n-container"
        >
          <h3 class="mb-3 text-base font-medium text-n-slate-12">
            {{ $t('QUEUED_CUSTOMERS_REPORTS.CHARTS.OUTCOMES') }}
          </h3>
          <div class="h-64">
            <BarChart
              :collection="outcomesCollection"
              show-legend
              :chart-options="stackedOptions"
              @bar-click="onOutcomesBarClick"
            />
          </div>
        </section>
      </div>

      <section
        class="overflow-hidden rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
      >
        <div
          class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak px-5 py-3"
        >
          <h3 class="mb-0 text-base font-medium text-n-slate-12">
            {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.TITLE') }}
          </h3>
          <div class="flex gap-1">
            <button
              type="button"
              class="rounded-lg px-2.5 py-1 text-xs"
              :class="
                summaryGroupBy === 'source'
                  ? 'bg-n-brand text-white'
                  : 'border border-n-weak text-n-slate-11'
              "
              @click="
                summaryGroupBy = 'source';
                fetchReport();
              "
            >
              {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.SOURCE') }}
            </button>
            <button
              type="button"
              class="rounded-lg px-2.5 py-1 text-xs"
              :class="
                summaryGroupBy === 'team'
                  ? 'bg-n-brand text-white'
                  : 'border border-n-weak text-n-slate-11'
              "
              @click="
                summaryGroupBy = 'team';
                fetchReport();
              "
            >
              {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.TEAM') }}
            </button>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full table-fixed text-xs">
            <thead>
              <tr class="border-b border-n-weak text-n-slate-11">
                <th class="px-3 py-2 text-left font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.ENTITY') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.ENTRIES') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.UNIQUE') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.ACCEPTED') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.ABANDONED') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.WAITING') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.ABANDON_RATE') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.AVG_WAIT') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.MEDIAN_WAIT') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.MAX_WAIT') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('QUEUED_CUSTOMERS_REPORTS.SUMMARY.PEAK') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in report.summary"
                :key="`${row.group_type}-${row.id}`"
                class="border-b border-n-weak/60 cursor-pointer hover:bg-n-alpha-1"
                @click="onSummaryRowClick(row)"
              >
                <td class="px-3 py-2 text-n-slate-12">{{ row.name }}</td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.entries }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.unique_inquiries }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.accepted }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.abandoned }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.waiting_at_end }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ formatRate(row.abandonment_rate) }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ formatWait(row.average_wait) }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ formatWait(row.median_wait) }}
                </td>
                <td class="px-3 py-2 text-center">
                  <button
                    v-if="row.max_wait_conversation_id"
                    type="button"
                    class="text-n-brand hover:underline"
                    @click="
                      onMaxWaitClick($event, row.max_wait_conversation_id)
                    "
                  >
                    {{ formatWait(row.max_wait) }}
                  </button>
                  <span v-else>{{ formatWait(row.max_wait) }}</span>
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.peak_size }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <div
      v-if="loading"
      class="fixed inset-0 z-40 flex items-start justify-center pt-[12.5rem] bg-n-solid-1/40 pointer-events-none"
    >
      <Spinner :size="32" class="text-n-brand" />
    </div>
  </div>
</template>
